import { useEffect, useRef } from 'react';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';

interface AudioPlaybackParams {
  layers: CompositorNodeV3['data']['layers'] | undefined;
  layerOrder: CompositorNodeV3['data']['layerOrder'] | undefined;
  compositorInputs: CompositorNodeV3['data']['input'] | undefined;
  currentTime: number;
  isPlaying: boolean;
  globalMuted: boolean;
}

/**
 * Manages HTML Audio elements for audio layers in the compositor.
 * Syncs playback state (time, play/pause, mute) with the timeline.
 */
export function useAudioPlayback({
  layers,
  layerOrder,
  compositorInputs,
  currentTime,
  isPlaying,
  globalMuted,
}: AudioPlaybackParams) {
  const audioElementsRef = useRef<Map<number, HTMLAudioElement>>(new Map());

  useEffect(() => {
    const audioElements = audioElementsRef.current;

    if (!layerOrder || !layers) {
      return;
    }

    // Collect audio layers and their URLs
    const activeAudioLayers = new Map<
      number,
      { url: string; startTime: number; trimStart: number; duration: number; muted: boolean; visible: boolean }
    >();

    for (const layerId of layerOrder) {
      const layer = layers[layerId];
      if (!layer || layer.kind.type !== 'audio') continue;

      const matchedInput = compositorInputs?.find(
        ([inputId, value]) => inputId === layer.kind.inputId && value != null,
      );
      if (!matchedInput) continue;

      const file = matchedInput[1]?.file;
      if (!file || file.type !== 'audio') continue;

      activeAudioLayers.set(layerId, {
        url: file.url,
        startTime: layer.startTime ?? 0,
        trimStart: layer.kind.trimStart ?? 0,
        duration: layer.duration ?? 0,
        muted: layer.kind.muted,
        visible: layer.visible,
      });
    }

    // Remove audio elements for layers that no longer exist
    for (const [layerId, audio] of audioElements) {
      if (!activeAudioLayers.has(layerId)) {
        audio.pause();
        audio.src = '';
        audioElements.delete(layerId);
      }
    }

    // Create or update audio elements
    for (const [layerId, info] of activeAudioLayers) {
      let audio = audioElements.get(layerId);
      if (!audio) {
        audio = new Audio();
        audio.preload = 'auto';
        audioElements.set(layerId, audio);
      }

      if (audio.src !== info.url) {
        audio.src = info.url;
      }

      const layerMuted = info.muted || globalMuted || !info.visible;
      audio.muted = layerMuted;

      // Check if current time is within this layer's range
      const layerStart = info.startTime;
      const layerEnd = info.startTime + info.duration;
      const isInRange = currentTime >= layerStart && currentTime < layerEnd;

      if (isInRange && isPlaying) {
        const audioTime = currentTime - layerStart + info.trimStart;
        // Only seek if significantly out of sync (avoid micro-seeks during playback)
        if (Math.abs(audio.currentTime - audioTime) > 0.3) {
          audio.currentTime = audioTime;
        }
        if (audio.paused) {
          void audio.play();
        }
      } else {
        if (!audio.paused) {
          audio.pause();
        }
        if (isInRange) {
          // Paused but in range — sync position for when play resumes
          const audioTime = currentTime - layerStart + info.trimStart;
          audio.currentTime = audioTime;
        }
      }
    }
  }, [layers, layerOrder, compositorInputs, currentTime, isPlaying, globalMuted]);

  // Cleanup on unmount
  useEffect(() => {
    const audioElements = audioElementsRef.current;
    return () => {
      for (const [, audio] of audioElements) {
        audio.pause();
        audio.src = '';
      }
      audioElements.clear();
    };
  }, []);
}
